import React, { useMemo, useState } from 'react';
import {
  LineChart,
  Line,
  Tooltip as ChartTooltip,
  ResponsiveContainer,
  YAxis,
  XAxis,
} from 'recharts';
import { Info } from 'lucide-react';
import { DashboardWidgetWrapper } from '@/pages/organization/components/DashboardWidgetWrapper';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  getDefaultTimeRange,
  TimeRangeSelector,
  type TimeFilter,
} from '@/components/TimeRangeSelector';
import type { DisplayTimeZone } from '@/components/TimeRangeSelector/types';
import {
  formatDateInDisplayTimeZone,
  getBrowserTimeZone,
} from '@/components/TimeRangeSelector/utils';
import { useOrganizationDashboardUsage } from '@/hooks/api/useOrganizationDashboardUsage';
import { useOrganization } from '@/hooks/api/useOrganizations';
import { useParams } from 'react-router-dom';

const COVERED_ACTIVE_LABEL = 'Covered Active Users';
const COVERAGE_GAPS_LABEL = 'Active Users with Coverage Gaps';
const TOTAL_USERS_LABEL = 'Total Users';
const COVERED_ACTIVE_STROKE = 'var(--color-primary)';
const COVERAGE_GAPS_STROKE = 'var(--color-muted-foreground)';
const TOTAL_USERS_STROKE = 'var(--color-chart-3)';

function KirinUsersTooltip({
  active,
  payload,
  timezone,
}: {
  active?: boolean;
  payload?: {
    payload: {
      coveredActive: number;
      coverageGaps: number;
      totalUsers: number;
      date: string;
    };
  }[];
  timezone: DisplayTimeZone;
}): React.ReactElement | null {
  if (!active || !payload || !payload.length) return null;

  const { coveredActive, coverageGaps, totalUsers, date } = payload[0].payload;

  return (
    <div className="bg-background border border-border shadow-lg rounded-lg px-3 py-2 shadow-lg">
      <div className="flex items-center gap-2 text-sm">
        <span
          aria-hidden
          className="inline-block h-2 w-2 rounded-full"
          style={{ backgroundColor: COVERED_ACTIVE_STROKE }}
        />
        <span className="font-semibold text-foreground">{coveredActive}</span>
        <span className="text-muted-foreground">{COVERED_ACTIVE_LABEL}</span>
      </div>
      <div className="flex items-center gap-2 text-sm">
        <span
          aria-hidden
          className="inline-block h-2 w-2 rounded-full"
          style={{ backgroundColor: COVERAGE_GAPS_STROKE }}
        />
        <span className="font-semibold text-foreground">{coverageGaps}</span>
        <span className="text-muted-foreground">{COVERAGE_GAPS_LABEL}</span>
      </div>
      <div className="flex items-center gap-2 text-sm">
        <span
          aria-hidden
          className="inline-block h-2 w-2 rounded-full"
          style={{ backgroundColor: TOTAL_USERS_STROKE }}
        />
        <span className="font-semibold text-foreground">{totalUsers}</span>
        <span className="text-muted-foreground">{TOTAL_USERS_LABEL}</span>
      </div>
      <div className="text-xs text-muted-foreground mt-1">
        {formatDateInDisplayTimeZone(date, timezone, {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
        })}
      </div>
    </div>
  );
}

export const CoverageOverTime: React.FC = () => {
  const { organizationId } = useParams<{ organizationId: string }>();
  const { data: organization } = useOrganization(organizationId);
  const [timeFilter, setTimeFilter] = useState<TimeFilter>({
    range: getDefaultTimeRange('browser'),
    timezone: 'browser',
  });

  const fromParam = timeFilter.range.from.toISOString();
  const toParam = timeFilter.range.to.toISOString();
  const resolvedTz =
    timeFilter.timezone === 'browser'
      ? getBrowserTimeZone()
      : timeFilter.timezone;
  const isEnabled = Boolean(organizationId && fromParam && toParam);

  const { data, isLoading, isError, refetch } = useOrganizationDashboardUsage(
    organizationId,
    fromParam,
    toParam,
    isEnabled,
    resolvedTz
  );

  const chartData = useMemo(
    () =>
      (data?.history ?? []).map(bucket => ({
        date: bucket.date,
        coveredActive: bucket.coveredActive,
        coverageGaps: bucket.coverageGaps,
        totalUsers: bucket.totalUsers,
      })),
    [data]
  );

  // Total distinct covered-active users across the selected window (not the
  // last bucket) — see meta.total from the usage endpoint.
  const totalCoveredUsers = data?.meta.total ?? 0;

  // Total activated users — matches the Inventory Users tab count.
  const totalUsers = data?.meta.totalUsers ?? 0;

  const currentCoverage = data?.meta.currentCoverage ?? 0;
  const previousCoverage = data?.meta.previousCoverage ?? 0;

  const trendPercent = useMemo((): number => {
    if (previousCoverage === 0) {
      return currentCoverage > 0 ? 100 : 0;
    }
    return ((currentCoverage - previousCoverage) / previousCoverage) * 100;
  }, [currentCoverage, previousCoverage]);

  const trendStr = `${trendPercent > 0 ? '+ ' : ''}${trendPercent.toFixed(1)}%`;
  const trendColorClass =
    trendPercent > 0
      ? 'text-status-success-foreground'
      : trendPercent < 0
        ? 'text-destructive'
        : 'text-muted-foreground';

  return (
    <DashboardWidgetWrapper
      className="xl:col-span-7"
      title="Kirin User Coverage Over Time"
      isLoading={isLoading}
      isError={isError}
      refetch={refetch}
    >
      <div className="flex flex-col h-full w-full">
        {/* Header */}
        <div className="mb-4 w-full flex justify-end gap-8">
          <div className="flex flex-col gap-1 items-end">
            <div className="flex items-center gap-1 text-xs text-muted-foreground font-medium">
              <span>{TOTAL_USERS_LABEL}</span>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    aria-label="Total Users — more info"
                    className="inline-flex items-center text-muted-foreground hover:text-foreground focus:outline-none"
                  >
                    <Info className="h-3.5 w-3.5" aria-hidden />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-xs">
                  Total activated users — all users with Kirin activity in this
                  organization (matches the Inventory Users count)
                </TooltipContent>
              </Tooltip>
            </div>
            <div className="text-3xl font-bold text-foreground">
              {totalUsers}
            </div>
          </div>
          <div className="flex flex-col gap-1 items-end">
            <div className="flex items-center gap-1 text-xs text-muted-foreground font-medium">
              <span>Covered Users Reporting Activity</span>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    aria-label="Covered Users Reporting Activity — more info"
                    className="inline-flex items-center text-muted-foreground hover:text-foreground focus:outline-none"
                  >
                    <Info className="h-3.5 w-3.5" aria-hidden />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-xs">
                  Covered Active Users - Users who are covered by Kirin and had
                  activity during the selected time period
                </TooltipContent>
              </Tooltip>
            </div>
            <div className="text-3xl font-bold text-foreground">
              {totalCoveredUsers}
            </div>
          </div>
        </div>

        {/* Chart — explicit min size avoids ResponsiveContainer measuring -1 on reload before flex layout settles */}
        <div
          className="h-[220px] w-full min-w-0"
          onMouseDown={e => e.preventDefault?.()}
        >
          <ResponsiveContainer width="100%" height="100%" minWidth={0}>
            <LineChart
              data={chartData}
              margin={{ top: 12, right: 12, left: 0, bottom: 0 }}
            >
              <XAxis dataKey="date" hide padding={{ left: 7, right: 20 }} />
              <YAxis hide domain={[0, 'dataMax + 2']} />

              <ChartTooltip
                content={<KirinUsersTooltip timezone={timeFilter.timezone} />}
                cursor={false}
              />

              <Line
                type="monotone"
                dataKey="coveredActive"
                name={COVERED_ACTIVE_LABEL}
                stroke={COVERED_ACTIVE_STROKE}
                strokeWidth={3}
                dot={false}
                strokeLinecap="round"
                strokeLinejoin="round"
                activeDot={(props: { cx?: number; cy?: number }) => {
                  const { cx, cy } = props;
                  if (cx == null || cy == null) return <g />;
                  return (
                    <g>
                      <rect
                        x={cx - 9}
                        y={cy - 5}
                        width={18}
                        height={10}
                        rx={6}
                        ry={6}
                        fill={COVERED_ACTIVE_STROKE}
                      />
                    </g>
                  );
                }}
              />
              <Line
                type="monotone"
                dataKey="coverageGaps"
                name={COVERAGE_GAPS_LABEL}
                stroke={COVERAGE_GAPS_STROKE}
                strokeWidth={2}
                strokeDasharray="4 4"
                dot={false}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <Line
                type="stepAfter"
                dataKey="totalUsers"
                name={TOTAL_USERS_LABEL}
                stroke={TOTAL_USERS_STROKE}
                strokeWidth={2}
                dot={false}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Legend */}
        <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
          <div className="flex items-center gap-1.5">
            <span
              aria-hidden
              className="inline-block h-0.5 w-4 rounded"
              style={{ backgroundColor: COVERED_ACTIVE_STROKE }}
            />
            <span>{COVERED_ACTIVE_LABEL}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span
              aria-hidden
              className="inline-block h-0.5 w-4 rounded border-t border-dashed"
              style={{ borderColor: COVERAGE_GAPS_STROKE }}
            />
            <span>{COVERAGE_GAPS_LABEL}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span
              aria-hidden
              className="inline-block h-0.5 w-4 rounded"
              style={{ backgroundColor: TOTAL_USERS_STROKE }}
            />
            <span>{TOTAL_USERS_LABEL}</span>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center gap-1.5 mt-3">
          <span className={`text-sm font-semibold ${trendColorClass}`}>
            {trendStr}
          </span>
          <TimeRangeSelector
            timeFilter={timeFilter}
            onChange={setTimeFilter}
            minDate={organization?.createdAt}
            presetsOnly
          />
        </div>
      </div>
    </DashboardWidgetWrapper>
  );
};
